/**
 * WHAT THE IN-APP MESSAGE OVERLAY DOES (D-92's light-only surfaces: the overlay, part 1, 2026-10-26).
 *
 * The overlay App.tsx mounts beside the chat (src/components/InAppMessageOverlay.tsx) shows a signed-in person the
 * platform's messages: what `send_platform_message_to_user` stored under users/{uid}/platform_messages. Everything it
 * does is here, moved verbatim and in its original order from that file, so that today's overlay and the new design's
 * (src/design/pavilion/appmessages/) draw the same view and cannot do different things:
 *  - who it is for (the signed-in person, followed live);
 *  - which messages it shows (the platform tool's, never an expired one, never the planner's "could not reach the
 *    planning service" line), each one delivered once, and each NEW one handed to the chat (never the first snapshot's,
 *    never one tagged with a session: the landing page's own listener takes those);
 *  - on a developer's machine, the local queue's messages handed to the chat;
 *  - the stack held at its newest message, and the minute's sweep of messages that expired since they arrived;
 *  - what each button does, the inline form a "form" button opens and what its submit sends, and the "view" a pointer
 *    over a message's words records.
 * What changed at the seams: the hook's signature and the view it returns; the three things the drawing did inline (the
 * view's record, the form's cancel, the body as stored), each now a function of the view (each drawing still renders the
 * body through the chat's sanitizer at its own sink, as today's did, where scripts/check-html-sinks.mjs can see it); the stack's
 * ref, now a callback that also starts a stack drawn after the messages arrived (the new design's, once its chunk is
 * here) at the newest; and the confirm box's words, now IN_APP_MESSAGE_WORDS's.
 * tests/landing/inAppMessages/inAppMessagesKeepWhatTheyDo.test.tsx pins it from the outside, through the module App.tsx
 * imports: it passed against the overlay before the move and passes after it.
 */
import React from 'react';
import { inCurrentTree } from '../../design/previewPath';
import { onSnapshot, query, orderBy, Unsubscribe, deleteDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { Paths } from '../../repositories/firestoreUtils';
import { useNavigate } from 'react-router-dom';
import { followProvidedLink } from '../../lib/linkLanding';
import { IN_APP_MESSAGE_WORDS } from '../../components/inAppMessageWords';

export type MessageType = 'notification' | 'alert' | 'info' | 'warning' | 'error' | 'success' | 'system';
export type PriorityLevel = 'urgent' | 'high' | 'normal' | 'low';

export type ActionButton = { button_text: string; action_type: string; action_value: string; button_style: string };

export type MessageDoc = {
  message_id: string;
  user_id: string;
  message_type: MessageType;
  priority_level: PriorityLevel;
  created_at_iso?: string;
  expires_at_iso?: string | null;
  content?: { markdown?: string; html?: string };
  action_buttons?: ActionButton[];
  read?: boolean;
  in_app?: { status?: string; delivered_at_iso?: string };
};

/** The inline form a message's "form" button opened: whose it is, and what it asks for. */
export type ActiveForm = { messageId: string; spec: string };

/** What either design draws, and the handlers its presses call. */
export interface InAppMessagesView {
  /** Anything to draw: a signed-in person with at least one message. */
  shown: boolean;
  items: MessageDoc[];
  /** The stack's element: the hook keeps it at the newest message. */
  stackRef: (el: HTMLDivElement | null) => void;
  activeForm: ActiveForm | null;
  formValue: string;
  setFormValue: (value: string) => void;
  /**
   * A message's body as it was stored: its html, else its markdown. It is model output, stored verbatim, so a drawing
   * renders it only through the chat's sanitizer, at its own sink (scripts/check-html-sinks.mjs).
   */
  messageBody: (m: MessageDoc) => string;
  /** A pointer came over a message's words. */
  onView: (m: MessageDoc) => Promise<void>;
  onAction: (m: MessageDoc, btn: ActionButton) => Promise<void>;
  onSubmitForm: (e: React.FormEvent) => Promise<void>;
  onCancelForm: () => void;
}

function useCurrentUid(): string | null {
  const [uid, setUid] = React.useState<string | null>(null);
  React.useEffect(() => {
    const auth = getAuth();
    const cur = auth.currentUser?.uid || null;
    setUid(cur);
    const unsub = auth.onAuthStateChanged((u) => setUid(u?.uid || null));
    return () => { try { unsub(); } catch {} };
  }, []);
  return uid;
}

export function isExpired(item: MessageDoc): boolean {
  if (!item?.expires_at_iso) return false;
  const t = Date.parse(item.expires_at_iso);
  if (Number.isNaN(t)) return false;
  return Date.now() > t;
}

/** A message's body as it was stored: its html, else its markdown (untrusted: see InAppMessagesView.messageBody). */
export function messageBody(m: MessageDoc): string {
  return String(m?.content?.html || m?.content?.markdown || '');
}

export function useInAppMessages(): InAppMessagesView {
  const uid = useCurrentUid();
  const [items, setItems] = React.useState<MessageDoc[]>([]);
  const navigate = useNavigate();
  const stackEl = React.useRef<HTMLDivElement | null>(null);
  const [activeForm, setActiveForm] = React.useState<ActiveForm | null>(null);
  const [formValue, setFormValue] = React.useState<string>('');

  // Realtime listener
  React.useEffect(() => {
    if (!uid) return;
    const col = Paths.userPlatformMessagesCol(uid);
    const qy = query(col, orderBy('created_at', 'asc'));
    let unsub: Unsubscribe | undefined;
    // PRE-70: Track dispatched message IDs to avoid re-dispatching the full collection on every snapshot.
    // Without this, switching threads causes all historical messages to re-dispatch and bleed into the new thread.
    const dispatchedIds = new Set<string>();
    let isFirstSnapshot = true;
    unsub = onSnapshot(qy, (snap) => {
      const next: MessageDoc[] = snap.docs.map(d => ({ message_id: d.id, ...(d.data() as any) }));
      const filtered = next
        .filter(x => !isExpired(x))
        .filter(x => String((x as any)?.source_tool || '') === 'send_platform_message_to_user')
        .filter(x => !String((x as any)?.content?.markdown || '').toLowerCase().includes('we could not reach the planning service'));
      setItems(filtered);
      // PRE-70: Only dispatch NEW messages (not yet dispatched) into the chat thread.
      // On first snapshot, mark all existing messages as "already dispatched" — localStorage restore
      // handles historical messages, so we only want truly new arrivals dispatched as events.
      try {
        if (isFirstSnapshot) {
          filtered.forEach(m => dispatchedIds.add(m.message_id));
          isFirstSnapshot = false;
        } else {
          const newMessages = filtered.filter(m => !dispatchedIds.has(m.message_id));
          if (newMessages.length > 0) {
            newMessages.forEach(m => dispatchedIds.add(m.message_id));
            // PRE-70: Skip dispatching session-tagged messages — LandingPage has its own
            // session-scoped Firestore listener that handles those directly. Only dispatch
            // untagged messages (non-workflow) through this broadcast path.
            const untaggedMessages = newMessages.filter((m) => !(m as any)?.metadata?.session_id);
            if (untaggedMessages.length > 0) {
              const chatItems = untaggedMessages.map((m) => ({ text: String((m as any)?.content?.markdown || ''), html: String((m as any)?.content?.html || ''), isUser: false, timestamp: new Date(), session_id: undefined }));
              window.dispatchEvent(new CustomEvent('chat:add-platform-messages', { detail: chatItems }));
            }
          }
        }
      } catch {}
      // Mark delivered for new messages
      const nowIso = new Date().toISOString();
      filtered.forEach(async (m) => {
        if (!(m as any)?.in_app?.delivered_at_iso) {
          try { await setDoc(Paths.userPlatformMessageDoc(uid, m.message_id), { in_app: { status: 'DELIVERED', delivered_at: serverTimestamp(), delivered_at_iso: nowIso } }, { merge: true }); } catch {}
          try { await setDoc(Paths.userPlatformMessageEventDoc(uid, m.message_id, `${Date.now()}`), { event: 'delivered', channel: 'in_app', ts: nowIso }); } catch {}
        }
      });
    }, (err) => {
      // eslint-disable-next-line no-console
      console.warn('InAppMessageOverlay listener error:', err?.message || err);
    });
    return () => { try { unsub && unsub(); } catch {} };
  }, [uid]);

  // Fallback polling of API queue so chat receives messages even without Firestore/Admin
  React.useEffect(() => {
    let active = true;
    async function poll() {
      if (!active || !uid) return;
      try {
        const host = (window.location.hostname || '').toLowerCase();
        const isLocal = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
        if (!isLocal) return; // Production uses Firestore real-time listeners
        const res = await fetch(`http://127.0.0.1:3001/api/pull-inapp-messages?uid=${encodeURIComponent(uid)}`);
        const data = await res.json().catch(() => ({ ok: false }));
        if (data && data.ok && Array.isArray(data.items) && data.items.length) {
          const chatItems = data.items.map((it: any) => ({ text: String(it?.text || ''), html: String(it?.html || ''), isUser: false, timestamp: new Date() }));
          try { window.dispatchEvent(new CustomEvent('chat:add-platform-messages', { detail: chatItems })); } catch {}
        }
      } catch {}
      if (active) setTimeout(poll, 1500);
    }
    poll();
    return () => { active = false; };
  }, [uid]);

  // Auto-scroll to newest
  React.useEffect(() => {
    try {
      const el = stackEl.current;
      if (el) { el.scrollTop = el.scrollHeight; }
    } catch {}
  }, [items.length]);

  // A stack drawn after its messages arrived (the new design's, once its chunk is here) starts at the newest too.
  const stackRef = React.useCallback((el: HTMLDivElement | null) => {
    stackEl.current = el;
    try { if (el) { el.scrollTop = el.scrollHeight; } } catch {}
  }, []);

  // Expiration cleanup timer
  React.useEffect(() => {
    if (!uid) return;
    const t = window.setInterval(async () => {
      const expired = items.filter(isExpired);
      for (const m of expired) {
        try { await deleteDoc(Paths.userPlatformMessageDoc(uid, m.message_id)); } catch {}
      }
    }, 60 * 1000);
    return () => { window.clearInterval(t); };
  }, [uid, items]);

  const handleAction = async (m: MessageDoc, btn: ActionButton) => {
    const kind = String(btn.action_type || '').toLowerCase();
    const val = String(btn.action_value || '');
    try {
      // Log event
      const logRef = Paths.userActionEventDoc(m.user_id, `${m.message_id}:${Date.now()}`);
      try { await setDoc(logRef, { message_id: m.message_id, action_type: kind, action_value: val, ts: new Date().toISOString() }, { merge: false }); } catch {}

      switch (kind) {
        // a provided link lands on its page (src/lib/linkLanding.ts); what it does not take stays as it was
        case 'navigate':
        case 'url':
          if (!followProvidedLink(val)) navigate(val);
          break;
        case 'open_url':
          if (!followProvidedLink(val, {}, { target: '_blank' })) window.open(val, '_blank', 'noopener,noreferrer');
          break;
        case 'deep_link':
          window.location.href = inCurrentTree(val);
          break;
        case 'copy_text':
          await navigator.clipboard.writeText(val);
          break;
        case 'dismiss':
          await deleteDoc(Paths.userPlatformMessageDoc(m.user_id, m.message_id));
          break;
        case 'confirm': {
          const ok = window.confirm(IN_APP_MESSAGE_WORDS.confirm);
          if (ok) {
            const reqId = `${m.message_id}:confirm:${Date.now()}`;
            await setDoc(Paths.userActionRequestDoc(m.user_id, reqId), { message_id: m.message_id, action_type: 'confirm', action_value: val, requested_at: new Date().toISOString(), state: 'REQUESTED' });
          }
          break;
        }
        case 'form':
          setActiveForm({ messageId: m.message_id, spec: val });
          setFormValue('');
          break;
        case 'callback':
        case 'custom': {
          // Enqueue action request for backend processing/tools
          const reqId = `${m.message_id}:${kind}:${Date.now()}`;
          await setDoc(Paths.userActionRequestDoc(m.user_id, reqId), { message_id: m.message_id, action_type: kind, action_value: val, requested_at: new Date().toISOString(), state: 'REQUESTED' });
          break;
        }
        default:
          break;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Action failed', e);
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const af = activeForm; if (!af || !uid) return;
    const reqId = `${af.messageId}:form:${Date.now()}`;
    try {
      await setDoc(Paths.userActionRequestDoc(uid, reqId), { message_id: af.messageId, action_type: 'form', action_value: af.spec, form_value: formValue, requested_at: new Date().toISOString(), state: 'REQUESTED' });
      setActiveForm(null);
      setFormValue('');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Form submit failed', e);
    }
  };

  const handleView = async (m: MessageDoc) => {
    try { await setDoc(Paths.userPlatformMessageEventDoc(m.user_id, m.message_id, `${Date.now()}`), { event: 'view', ts: new Date().toISOString() }); } catch {}
  };

  return {
    shown: !!uid && items.length > 0,
    items,
    stackRef,
    activeForm,
    formValue,
    setFormValue,
    messageBody,
    onView: handleView,
    onAction: handleAction,
    onSubmitForm: handleSubmitForm,
    onCancelForm: () => setActiveForm(null),
  };
}
